<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  getTime,
  timeInMinutes,
  slotWorkingIntervals,
} from '../helpers/businessHour';
import { formatMinuteOfDay } from 'shared/helpers/businessHours';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import BusinessTimeInput from './BusinessTimeInput.vue';

const props = defineProps({
  dayName: { type: String, required: true },
  timeSlot: { type: Object, required: true },
  lunchStartMinute: { type: Number, default: null },
  lunchEndMinute: { type: Number, default: null },
});
const emit = defineEmits(['update']);
const { t } = useI18n();
const DEFAULT_OPENING = getTime(8, 0);
const DEFAULT_CLOSING = getTime(18, 0);

const isDayEnabled = computed({
  get: () => props.timeSlot.enabled,
  set: enabled =>
    emit('update', {
      ...props.timeSlot,
      enabled,
      from: enabled ? DEFAULT_OPENING : '',
      to: enabled ? DEFAULT_CLOSING : '',
      valid: enabled,
      openAllDay: false,
    }),
});
const fromTime = computed({
  get: () => props.timeSlot.from,
  set: value =>
    emit('update', {
      ...props.timeSlot,
      from: value,
      valid: timeInMinutes(value) < timeInMinutes(props.timeSlot.to),
    }),
});
const toTime = computed({
  get: () => props.timeSlot.to,
  set: value =>
    emit('update', {
      ...props.timeSlot,
      to: value,
      valid: timeInMinutes(value) > timeInMinutes(props.timeSlot.from),
    }),
});
const isOpenAllDay = computed({
  get: () => props.timeSlot.openAllDay,
  set: enabled =>
    emit('update', {
      ...props.timeSlot,
      from: enabled ? getTime(0, 0) : DEFAULT_OPENING,
      to: enabled ? getTime(23, 59) : DEFAULT_CLOSING,
      valid: true,
      openAllDay: enabled,
    }),
});
const intervals = computed(() =>
  slotWorkingIntervals(
    props.timeSlot,
    props.lunchStartMinute,
    props.lunchEndMinute
  )
);
const hasError = computed(
  () => !props.timeSlot.valid || !intervals.value.length
);
const totalHours = computed(() => {
  const minutes = intervals.value.reduce(
    (sum, [start, end]) => sum + end - start,
    0
  );
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return (
    [hours && `${hours}h`, remainder && `${remainder}m`]
      .filter(Boolean)
      .join(' ') || '0m'
  );
});
const servicePeriods = computed(() =>
  intervals.value
    .map(
      ([start, end]) =>
        `${formatMinuteOfDay(start)} — ${formatMinuteOfDay(end)}`
    )
    .join(' · ')
);
</script>

<template>
  <tr>
    <td class="ps-4 pe-3 align-top">
      <label class="flex items-center gap-2 min-h-12">
        <input
          v-model="isDayEnabled"
          name="enable-day"
          class="m-0 accent-n-brand dark:[color-scheme:dark]"
          type="checkbox"
          :title="t('INBOX_MGMT.BUSINESS_HOURS.DAY.ENABLE')"
        />
        <span class="text-body-main text-n-slate-12 font-medium">{{
          dayName
        }}</span>
      </label>
    </td>
    <td class="py-2 pe-3">
      <div v-if="isDayEnabled" class="flex flex-col gap-1.5">
        <div class="flex flex-wrap items-center gap-3">
          <label class="flex items-center gap-2">
            <input
              v-model="isOpenAllDay"
              name="enable-open-all-day"
              class="m-0 accent-n-brand dark:[color-scheme:dark]"
              type="checkbox"
            />
            <span class="text-body-main text-n-slate-12">{{
              t('INBOX_MGMT.BUSINESS_HOURS.ALL_DAY')
            }}</span>
          </label>
          <BusinessTimeInput
            v-model="fromTime"
            :disabled="isOpenAllDay"
            :aria-label="`${dayName}: ${t('BUSINESS_HOURS_SETTINGS.START_TIME')}`"
          />
          <Icon icon="i-lucide-minus" class="size-3 text-n-slate-11" />
          <BusinessTimeInput
            v-model="toTime"
            :disabled="isOpenAllDay"
            :aria-label="`${dayName}: ${t('BUSINESS_HOURS_SETTINGS.END_TIME')}`"
          />
        </div>
        <span v-if="hasError" class="text-label-small text-n-ruby-9">
          {{
            timeSlot.valid
              ? t('BUSINESS_HOURS_SETTINGS.NO_SERVICE_PERIOD')
              : t('INBOX_MGMT.BUSINESS_HOURS.DAY.VALIDATION_ERROR')
          }}
        </span>
        <p
          v-else-if="lunchStartMinute !== null"
          class="m-0 text-label-small text-n-slate-11"
        >
          {{ t('BUSINESS_HOURS_SETTINGS.WORKING_PERIODS') }}
          {{ servicePeriods }}
        </p>
      </div>
      <span v-else class="text-body-main text-n-slate-11">{{
        t('INBOX_MGMT.BUSINESS_HOURS.DAY.UNAVAILABLE')
      }}</span>
    </td>
    <td class="py-2 pe-3 align-top">
      <span
        v-if="isDayEnabled && !hasError"
        class="bg-n-blue-3 text-n-blue-11 text-label-small inline-block px-2 py-1 rounded-lg whitespace-nowrap"
      >
        {{ totalHours }}
      </span>
    </td>
  </tr>
</template>
